<template>
    <page :busy="busy" :tabs="tabs" class="amenities-manager-page">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :isSearch="false"
            :maxBlocksInCache="null"
            :headerHeight="50"
            :rowBuffer="30"
        >
            <form-input
                v-model="search_property"
                @input="handleSearchProperty"
                placeholder="Filter by Property"
                class="mr-md-2 toolbar-input"
            ></form-input>
            <form-input
                v-model="search"
                @input="handleSearchAmenity"
                placeholder="Filter by Amenity"
                class="mr-md-2 mr-0 toolbar-input"
            ></form-input>
            <form-select
                v-model="amenity_category"
                @change="handleChangeCategory"
                :options="amenityCategoriesOptions"
                class="toolbar-input"
            ></form-select>
        </grid>
    </page>
</template>

<script>
    import API from '../../../api';
    import AmenitiesCellRenderer from './amenities-cell-renderer.vue';
    import {
        AMENITIES_MANAGER_GRID_ENDPOINT,
        AMENITIES_MANAGER_PROPERTIES_WITH_AMENITIES_ENDPOINT,
        AMENITIES_MANAGER_SET_AMENITY_ENDPOINT,
        AMENITIES_MANAGER_REMOVE_AMENITY_ENDPOINT,
        AMENITIES_MANAGER_SET_ALL_PROPERTIES_ENDPOINT,
        AMENITIES_MANAGER_CATEGORIES_ENDPOINT,
        AMENITIES_MANAGER_REMOVE_ALL_PROPERTIES_ENDPOINT,
        AMENITIES_MANAGER_SET_SOME_PROPERTIES_ENDPOINT,
        AMENITIES_MANAGER_REMOVE_SOME_PROPERTIES_ENDPOINT,
    } from '../../../endpoints';
    import debounce from 'lodash/debounce';
    import grid from '../../generic/grid/grid.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import page from '../../generic/page/page.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';

    export default {
        components: {
            page,
            grid,
            ResponsiveDropdown,
            FormSelect,
            FormInput,
            amenitiesCellRenderer: AmenitiesCellRenderer,
        },
        data() {
            return {
                amenityCategories: [],
                amenity_category: null,
                busy: null,
                desc: false,
                filter: '',
                offset: 0,
                limit: 20,
                propertiesList: [],
                search_property: '',
                search: '',
                sort: 'id',
                tabs: [
                    {
                        id: 'policies',
                        title: 'Policies',
                        active: false,
                        route: '/app/policy/list/',
                    },
                    {
                        id: 'cancellation_policies',
                        title: 'Cancellation Policies',
                        active: false,
                        route: '/app/policy/cancellation-policy/list',
                    },
                    {
                        id: 'amenities_manager',
                        title: 'Amenities',
                        active: true,
                        route: '/app/policy/amenity-manager/list',
                    },
                    {
                        id: 'meta_data_manager',
                        title: 'Amenity Metadata',
                        active: false,
                        route: '/app/policy/meta-data/list',
                    },
                ],
            };
        },
        mounted() {
            Promise.all([this.fetchPropertiesList(true), this.fetchAmenityCategories()]);
            window.addEventListener('resize', this.handleResize);
            this.handleResize();
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.handleResize);
        },
        computed: {
            propertiesColumnDefs() {
                return this.propertiesList.map((item, index) => ({
                    colId: item.id,
                    field: item.name,
                    headerName: item.name,
                    sortable: false,
                    minWidth: 200,
                    cellRenderer: 'amenitiesCellRenderer',
                    valueGetter: ({ data }) => {
                        return !!item.amenities.find((i) => i === data.id);
                    },
                    cellRendererParams: {
                        onChange: async ({ api, node, data, column }, value) => {
                            const prevAllProperties = data.all_properties;
                            const prevAmenities = JSON.parse(JSON.stringify(item.amenities));
                            try {
                                if (!value) {
                                    node.updateData({
                                        ...node.data,
                                        all_properties: false,
                                    });
                                    item.amenities = item.amenities.filter((i) => i !== data.id);
                                } else {
                                    item.amenities.push(data.id);
                                    node.updateData({
                                        ...node.data,
                                        all_properties: this.propertiesList.every(
                                            (i) => i.amenities.indexOf(data.id) > -1
                                        ),
                                    });
                                }
                                await API.post(
                                    value
                                        ? AMENITIES_MANAGER_SET_AMENITY_ENDPOINT(data.id)
                                        : AMENITIES_MANAGER_REMOVE_AMENITY_ENDPOINT(data.id),
                                    {
                                        property_pk: item.id,
                                    }
                                );
                                node.data.all_properties &&
                                    !this.search_property &&
                                    (await API.post(AMENITIES_MANAGER_SET_ALL_PROPERTIES_ENDPOINT(data.id)));
                                if (data.category === 'Regional standard for sanitation practice') {
                                    this.fetchPropertiesList(true);
                                    this.$refs.grid.refreshDataSource();
                                }
                            } catch (e) {
                                node.updateData({ ...node.data, all_properties: prevAllProperties });
                                item.amenities = prevAmenities;
                                api.refreshCells({
                                    rowNodes: [node],
                                    columns: [column],
                                });
                                this.errorToast({
                                    text: `Could not change ${item.name}`,
                                });
                                return;
                            }
                        },
                    },
                }));
            },
            columnDefs() {
                return [
                    {
                        colId: 'name',
                        field: 'name',
                        headerName: 'Amenity Name',
                        headerClass: 'amenity-name-header-cell',
                        sortable: true,
                        minWidth: 220,
                        pinned: 'left',
                        resizable: false,
                        autoHeight: true,
                        sort: 'asc',
                        cellClass: 'amenity-name-cell',
                        cellRenderer: ({ data }) => {
                            const partners = data?.partners || [];

                            return `
                        <div class="d-flex flex-column align-items-start amenity-name-wrapper">
                            <span class="amenity-name">
                                ${data.name}
                            </span>
                            <div class="d-flex justify-content-around">
                                ${
                                    partners.includes('AIRBNB')
                                        ? '<img class="channel-icons mr-2" src="/static/images/channels/airbnb.png" alt="Airbnb.com" title="Airbnb.com" />'
                                        : ''
                                }
                                ${
                                    partners.includes('VRBO')
                                        ? ' <img class="channel-icons mr-2" src="/static/images/channels/vrbo.svg" alt="VRBO.com" title="VRBO.com" />'
                                        : ''
                                }
                                ${
                                    partners.includes('NEXTPAX')
                                        ? ' <img class="channel-icons mr-2" src="/static/images/channels/nextpax.png" alt="NextPax" title="NextPax" />'
                                        : ''
                                }
                            </div>
                         </div>
                        `;
                        },
                    },
                    this.propertiesList.length ? this.selectAllColumn : { field: '', headerName: 'No Property Found' },
                    ...this.propertiesColumnDefs,
                ];
            },
            selectAllColumn() {
                return {
                    colId: '',
                    field: 'all_properties',
                    headerName: '',
                    cellRenderer: 'amenitiesCellRenderer',
                    resizable: false,
                    valueGetter: ({ data }) => {
                        return data && data.all_properties;
                    },
                    sortable: false,
                    cellRendererParams: {
                        onChange: async ({ data, node }, value) => {
                            try {
                                node.updateData({ ...node.data, all_properties: value });
                                if (!this.search_property) {
                                    await API.post(
                                        value
                                            ? AMENITIES_MANAGER_SET_ALL_PROPERTIES_ENDPOINT(data.id)
                                            : AMENITIES_MANAGER_REMOVE_ALL_PROPERTIES_ENDPOINT(data.id)
                                    );
                                } else {
                                    await API.post(
                                        value
                                            ? AMENITIES_MANAGER_SET_SOME_PROPERTIES_ENDPOINT(data.id)
                                            : AMENITIES_MANAGER_REMOVE_SOME_PROPERTIES_ENDPOINT(data.id),
                                        {
                                            property_ids: this.propertiesList.map((item) => item.id),
                                        }
                                    );
                                }
                                this.fetchPropertiesList(true);
                                if (data.category === 'Regional standard for sanitation practice') {
                                    this.$refs.grid.refreshDataSource();
                                }
                            } catch (e) {
                                this.errorToast({
                                    text: `Could not set ${data.name}`,
                                });
                                node.updateData({ ...node.data, all_properties: !value });
                                return;
                            }
                        },
                    },
                };
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    return API.get(AMENITIES_MANAGER_GRID_ENDPOINT(), {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search: this.search,
                            category: this.amenity_category,
                            for_subscriber: true,
                        },
                    });
                };
            },
            amenityCategoriesOptions() {
                return [
                    { value: null, text: 'All Categories' },
                    ...this.amenityCategories.map((item) => ({
                        value: item,
                        text: item,
                    })),
                ];
            },
        },
        methods: {
            async fetchPropertiesList(withBusy) {
                try {
                    if (withBusy) {
                        this.busy = true;
                    }
                    const { data } = await API.get(AMENITIES_MANAGER_PROPERTIES_WITH_AMENITIES_ENDPOINT(), {
                        params: {
                            search_property: this.search_property,
                        },
                    });
                    this.propertiesList = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch properties' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchAmenityCategories() {
                try {
                    const { data } = await API.get(AMENITIES_MANAGER_CATEGORIES_ENDPOINT());
                    this.amenityCategories = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch amenity categories' });
                }
            },
            handleSearchProperty: debounce(function () {
                this.fetchPropertiesList(true);
            }, 500),
            handleSearchAmenity: debounce(function () {
                this.$refs.grid.refreshDataSource();
            }, 500),
            handleChangeCategory() {
                this.$refs.grid.refreshDataSource();
            },
            handleResize() {
                if (window.innerWidth < 768) {
                    this.$refs.grid.gridOptions.api.setColumnsPinned(['name'], false);
                } else {
                    this.$refs.grid.gridOptions.api.setColumnsPinned(['name'], true);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    .amenities-manager-page {
        :deep(.ag-header-cell) {
            .ag-header-cell-label {
                display: flex;
                justify-content: center;
                width: 100%;
            }
        }

        :deep(.amenity-name-header-cell) {
            border-right: 1px solid #cdcdcd;
        }
        :deep(.amenity-name-cell) {
            border-right: 1px solid #cdcdcd;
            padding-bottom: 5px;
            padding-top: 5px;
            .amenity-name {
                white-space: normal;
                word-break: break-word !important;
            }
            .channel-icons {
                width: 20px;
                height: 20px;
            }
            .ag-cell-wrapper {
                .ag-cell-value {
                    width: 100% !important;
                }
            }
        }
    }
    @include media-breakpoint-down(sm) {
        .amenities-manager-page {
            .toolbar-input {
                width: 100%;
            }
        }
    }
</style>
